import React, { useState, useEffect, useMemo, useRef } from "react";
import Dropdown from "components/dropdown";
import {
  FiAlignJustify,
  FiSearch,
  FiShare,
  FiPlusSquare,
  FiBell,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { RiMoonFill, RiSunFill } from "react-icons/ri";
import {
  MdLogout,
  MdOutlinePalette,
  MdKeyboardArrowDown,
  MdCheck,
  MdInstallMobile,
  MdNotificationsActive,
  MdClose,
  MdIosShare,
  MdAddBox,
} from "react-icons/md";

// --- İMPORTLAR ---
import { useAuth } from "context/AuthContext";
import { useThemeCustomizer } from "context/ThemeContext"; // TEMA MOTORU
import routes from "routes.js";
import { requestPermissionAndGetToken } from "firebase-helper";
import { usersAPI } from "services/api";

const Navbar = (props) => {
  const { onOpenSidenav, brandText } = props;
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // TEMA CONTEXT'İ
  const {
    isDark,
    setIsDark,
    brandTheme,
    setBrandTheme,
    navyTheme,
    setNavyTheme,
  } = useThemeCustomizer();

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false); // Profil menüsü içindeki Tema akordeonu için

  // YENİ EKLENEN PWA / BİLDİRİM STATE'LERİ
  const [isPwaModalOpen, setIsPwaModalOpen] = useState(false);
  const [pwaModalTab, setPwaModalTab] = useState("install"); // 'install' veya 'notify'

  const searchContainerRef = useRef(null);

  // --- KULLANICI BİLGİLERİ ---
  const getInitials = () => {
    if (!user) return "U";
    const first = user.name ? user.name.charAt(0) : "";
    const last = user.surname ? user.surname.charAt(0) : "";
    return `${first}${last}`.toUpperCase() || "U";
  };

  // --- ÇIKIŞ YAP ---
  const handleLogout = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Tıklamanın arkadaki butonlara sıçramasını ENGELLER!

    await logout();
    navigate("/auth/sign-in");
  };
  // --- ROL BAZLI ARAMA MOTORU ---
  const accessibleRoutes = useMemo(() => {
    const userRole = user?.role || "guest";
    let flatRoutes = [];

    routes.forEach((route) => {
      if (route.roles && !route.roles.includes(userRole)) return;

      if (route.layout && route.path && route.name) {
        flatRoutes.push({
          name: route.name,
          path: `${route.layout}/${route.path}`,
          icon: route.icon,
        });
      }

      if (route.items) {
        route.items.forEach((item) => {
          if (item.roles && !item.roles.includes(userRole)) return;
          if (item.path && item.name) {
            flatRoutes.push({
              name: item.name,
              path: `${route.layout}/${item.path}`,
              icon: route.icon,
            });
          }
        });
      }
    });
    return flatRoutes;
  }, [user]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return accessibleRoutes
      .filter((r) => r.name.toLowerCase().includes(searchQuery.toLowerCase()))
      .slice(0, 5);
  }, [searchQuery, accessibleRoutes]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- TEMA ŞABLONLARI ---
  const themePresets = [
    {
      id: "horizon",
      name: "Orijinal",
      brand: "default",
      navy: "default",
      brandHex: "#422AFB",
      navyHex: "#111C44",
    },
    {
      id: "luxury",
      name: "Lüks Altın",
      brand: "gold",
      navy: "espresso",
      brandHex: "#d97706",
      navyHex: "#1c1917",
    },
    {
      id: "forest",
      name: "Zümrüt",
      brand: "emerald",
      navy: "deepocean",
      brandHex: "#10b981",
      navyHex: "#082730",
    },
    {
      id: "soft",
      name: "Lavanta",
      brand: "lavender",
      navy: "slate",
      brandHex: "#8b5cf6",
      navyHex: "#1e293b",
    },
    {
      id: "soft-cyber",
      name: "Soft Cyberpunk",
      brand: "neon",
      navy: "soft-dark",
      brandHex: "#06b6d4",
      navyHex: "#18181b",
    },
    {
      id: "nordic-forest",
      name: "Kuzey Ormanı",
      brand: "forest",
      navy: "nordic",
      brandHex: "#059669",
      navyHex: "#243b53",
    },
    {
      id: "summer-peach",
      name: "Yaz Esintisi",
      brand: "peach",
      navy: "slate",
      brandHex: "#ff7d29",
      navyHex: "#1e293b",
    },
    {
      id: "nordic-sky",
      name: "Nordic Sky",
      brand: "lavender",
      navy: "nordic-pro",
      brandHex: "#8b5cf6",
      navyHex: "#1e2d41",
    },
    {
      id: "minimal-pro",
      name: "Minimalist",
      brand: "default",
      navy: "space-grey",
      brandHex: "#422AFB",
      navyHex: "#232326",
    },
    {
      id: "deep-slate",
      name: "Derin Füme",
      brand: "gold",
      navy: "midnight-slate",
      brandHex: "#d97706",
      navyHex: "#1e293b",
    },
  ];

  const applyPreset = (preset) => {
    setBrandTheme(preset.brand);
    setNavyTheme(preset.navy);
    setIsDark(true);
  };

  // --- GERÇEK BİLDİRİM İZNİ MANTIĞI ---
  const requestNotificationPermission = async (e) => {
    if (e) e.stopPropagation();

    if (!("Notification" in window)) {
      alert("Tarayıcınız bildirimleri desteklemiyor.");
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        // 1. İZİN ALINDIKTAN SONRA TOKEN'I ÜRET VE BACKEND'E YOLLA!
        const token = await requestPermissionAndGetToken();
        if (token) {
          try {
            await usersAPI.storeFcmToken(token, navigator.userAgent);
            console.log("Navbar üzerinden FCM Token başarıyla kaydedildi.");
          } catch (apiErr) {
            console.warn("Navbar Token kayıt hatası:", apiErr.message);
          }
        }

        alert(
          "Bildirim izni başarıyla alındı! Artık randevu onayları anında size ulaşacak."
        );
        setIsPwaModalOpen(false);
      } else if (permission === "denied") {
        alert("Bildirim izni reddedildi. Ayarlardan izin vermelisiniz.");
      }
    } catch (error) {
      console.error("Bildirim izni istenirken hata oluştu:", error);
    }
  };

  // NOT: Nav etiketinin dışına çıkabilmek için tüm yapıyı <React.Fragment> (<> ... </>) içine alıyoruz.
  return (
    <>
      <nav className="sticky top-4 z-40 flex flex-row flex-wrap items-center justify-between rounded-2xl bg-white/10 p-2 backdrop-blur-xl dark:bg-[#0b14374d]">
        {/* CSS: Gizli Scrollbar İçin */}
        <style>{`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>

        {/* SOL TARAF */}
        <div className="ml-[6px]">
          <p className="shrink text-[33px] capitalize text-navy-700 dark:text-white">
            <Link
              to="#"
              className="font-bold capitalize hover:text-navy-700 dark:hover:text-white"
            >
              {brandText}
            </Link>
          </p>
        </div>

        {/* SAĞ TARAF */}
        <div className="relative mt-[3px] flex h-[61px] w-[355px] flex-grow items-center justify-around gap-2 rounded-full bg-white px-2 py-2 shadow-xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none md:w-[365px] md:flex-grow-0 md:gap-1 xl:w-[365px] xl:gap-2">
          {/* ARAMA ÇUBUĞU */}
          <div
            ref={searchContainerRef}
            className="relative flex h-full items-center rounded-full bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white xl:w-[225px]"
          >
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </p>
            <input
              type="text"
              placeholder="Menüde Ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white sm:w-fit"
            />

            {isSearchFocused && searchResults.length > 0 && (
              <div className="absolute left-0 top-14 z-50 w-full rounded-2xl border border-gray-100 bg-white p-2 shadow-2xl dark:border-white/10 dark:bg-navy-800 dark:shadow-none">
                {searchResults.map((result, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      navigate(result.path);
                      setSearchQuery("");
                      setIsSearchFocused(false);
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-all hover:bg-gray-50 dark:hover:bg-navy-700"
                  >
                    <span className="text-brand-500 dark:text-white">
                      {result.icon}
                    </span>
                    <span className="text-sm font-bold text-navy-700 dark:text-white">
                      {result.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {isSearchFocused && searchQuery && searchResults.length === 0 && (
              <div className="absolute left-0 top-14 z-50 w-full rounded-2xl border border-gray-100 bg-white p-4 text-center text-sm font-medium text-gray-500 shadow-2xl dark:border-white/10 dark:bg-navy-800 dark:shadow-none">
                Sonuç bulunamadı.
              </div>
            )}
          </div>

          <span
            className="flex cursor-pointer rounded-full p-3 text-xl text-gray-600 transition-all hover:bg-gray-100 dark:text-white dark:hover:bg-navy-700 xl:hidden"
            onClick={onOpenSidenav}
          >
            <FiAlignJustify className="h-5 w-5" />
          </span>

          {/* SADECE CONTEXT'E BAĞLI DARK MODE ŞALTERİ */}
          <div
            className="cursor-pointer rounded-full p-3 text-gray-600 transition-all hover:bg-gray-100 dark:text-white dark:hover:bg-navy-700"
            onClick={() => setIsDark(!isDark)}
          >
            {isDark ? (
              <RiSunFill className="h-5 w-5 text-amber-500" />
            ) : (
              <RiMoonFill className="h-5 w-5 text-brand-500" />
            )}
          </div>

          {/* PROFİL & TEMA DROPDOWN */}
          <Dropdown
            button={
              <div className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-brand-500 text-sm font-bold text-white shadow-lg shadow-brand-500/30 transition-all hover:scale-105 dark:border-navy-800">
                {getInitials()}
              </div>
            }
            children={
              <div className="flex w-72 flex-col justify-start overflow-hidden rounded-[20px] border border-gray-100 bg-white bg-cover bg-no-repeat shadow-xl shadow-shadow-500 dark:border-white/10 dark:!bg-navy-700 dark:text-white dark:shadow-none">
                {/* Başlık */}
                <div className="p-4 pb-3">
                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                    👋 Merhaba, {user?.name || "Kullanıcı"}
                  </p>
                </div>
                <div className="h-px w-full bg-gray-200 dark:bg-white/10" />

                {/* TEMA SEÇİCİ AKORDEON */}
                <div className="flex flex-col">
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Menünün kapanmasını engeller
                      setIsThemeOpen(!isThemeOpen);
                    }}
                    className="flex w-full items-center justify-between p-4 pb-2 text-sm font-bold text-navy-700 transition-colors hover:text-brand-500 dark:text-white dark:hover:text-brand-400"
                  >
                    <div className="flex items-center gap-2">
                      <MdOutlinePalette className="text-lg" /> Görünüm Şablonu
                    </div>
                    <MdKeyboardArrowDown
                      className={`text-lg transition-transform duration-300 ${
                        isThemeOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Genişleyen Şablon (Skeleton) Alanı */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      isThemeOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="hide-scrollbar flex gap-3 overflow-x-auto px-4 pb-4 pt-2">
                      {themePresets.map((preset) => {
                        const isActive =
                          brandTheme === preset.brand &&
                          navyTheme === preset.navy;
                        return (
                          <div
                            key={preset.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              applyPreset(preset);
                            }}
                            className="group flex flex-shrink-0 cursor-pointer flex-col items-center gap-1.5"
                          >
                            {/* Mini Dashboard İskeleti (Skeleton) */}
                            <div
                              className={`flex h-14 w-[84px] overflow-hidden rounded-lg border-2 transition-transform group-hover:scale-105 ${
                                isActive
                                  ? "border-brand-500 shadow-md shadow-brand-500/30"
                                  : "border-gray-200 dark:border-white/10"
                              }`}
                            >
                              {/* Sol Menü / Brand Rengi */}
                              <div
                                className="h-full w-[30%]"
                                style={{ backgroundColor: preset.brandHex }}
                              ></div>
                              {/* Sağ İçerik / Navy Rengi */}
                              <div
                                className="flex h-full w-[70%] flex-col"
                                style={{ backgroundColor: preset.navyHex }}
                              >
                                <div className="h-3 w-full border-b border-white/10 bg-white/5" />
                                <div className="flex flex-1 items-end justify-end p-1">
                                  {isActive && (
                                    <MdCheck className="text-xs text-white drop-shadow-md" />
                                  )}
                                </div>
                              </div>
                            </div>
                            <span
                              className={`text-[10px] font-bold ${
                                isActive
                                  ? "text-brand-500 dark:text-brand-400"
                                  : "text-gray-500 dark:text-gray-400"
                              }`}
                            >
                              {preset.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="h-px w-full bg-gray-200 dark:bg-white/10" />

                {/* YENİ: UYGULAMA VE BİLDİRİM BUTONLARI */}
                <div className="flex flex-col gap-3 p-4 pb-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPwaModalTab("install");
                      setIsPwaModalOpen(true);
                    }}
                    className="flex items-center gap-2 text-sm font-bold text-navy-700 transition-colors hover:text-brand-500 dark:text-white dark:hover:text-brand-400"
                  >
                    <MdInstallMobile className="text-lg" /> Uygulamayı Yükle
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPwaModalTab("notify");
                      setIsPwaModalOpen(true);
                    }}
                    className="flex items-center gap-2 text-sm font-bold text-navy-700 transition-colors hover:text-brand-500 dark:text-white dark:hover:text-brand-400"
                  >
                    <MdNotificationsActive className="text-lg" /> Bildirimlere
                    İzin Ver
                  </button>
                </div>

                <div className="h-px w-full bg-gray-200 dark:bg-white/10" />

                {/* PROFİL & ÇIKIŞ */}
                <div className="flex flex-col gap-3 p-4">
                  <button
                    onClick={() =>
                      navigate(`/${user?.role || "admin"}/profile`)
                    }
                    className="text-left text-sm font-bold text-navy-700 transition-colors hover:text-brand-500 dark:text-white dark:hover:text-brand-400"
                  >
                    Profil Ayarları
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-sm font-bold text-red-500 transition-colors hover:text-red-600"
                  >
                    <MdLogout className="text-lg" /> Çıkış Yap
                  </button>
                </div>
              </div>
            }
            classNames={"py-2 top-8 -left-[240px] w-max"}
          />
        </div>
      </nav>

      {/* PWA & BİLDİRİM MODALI - (NAV'IN DIŞINDA YER ALIR) */}
      {isPwaModalOpen && (
        <div
          className="animate-fadeIn fixed inset-0 z-[99999] flex items-center justify-center bg-navy-900/60 p-4 backdrop-blur-sm transition-all"
          onClick={() => setIsPwaModalOpen(false)} // Arka plana tıklayınca kapanır
        >
          <div
            className="relative flex w-full max-w-md flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-navy-800"
            onClick={(e) => e.stopPropagation()} // İçeriğe tıklayınca kapanmasını engeller
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-gray-100 p-5 dark:border-navy-700">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                Uygulama Ayarları
              </h3>
              <button
                onClick={() => setIsPwaModalOpen(false)}
                className="rounded-full p-2 text-gray-400 transition-all hover:bg-gray-100 dark:hover:bg-navy-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            {/* Sekmeler (Tabs) */}
            <div className="flex gap-2 border-b border-gray-100 bg-gray-50/50 p-4 dark:border-navy-700 dark:bg-navy-900/30">
              <button
                onClick={() => setPwaModalTab("install")}
                className={`flex-1 rounded-xl py-2 text-sm font-bold transition-all ${
                  pwaModalTab === "install"
                    ? "bg-brand-500 text-white shadow-md shadow-brand-500/30"
                    : "bg-transparent text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-navy-700"
                }`}
              >
                Yükleme Rehberi
              </button>
              <button
                onClick={() => setPwaModalTab("notify")}
                className={`flex-1 rounded-xl py-2 text-sm font-bold transition-all ${
                  pwaModalTab === "notify"
                    ? "bg-brand-500 text-white shadow-md shadow-brand-500/30"
                    : "bg-transparent text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-navy-700"
                }`}
              >
                Bildirimler
              </button>
            </div>

            {/* Sekme İçerikleri */}
            <div className="p-6">
              {pwaModalTab === "install" ? (
                <div className="animate-fadeIn flex flex-col gap-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-500 dark:bg-brand-500/10">
                      <MdInstallMobile className="text-2xl" />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-navy-700 dark:text-white">
                        Uygulamamızı İndirin
                      </h4>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Daha hızlı ve akıcı bir deneyim için ana ekranınıza
                        ekleyin.
                      </p>
                    </div>
                  </div>

                  {/* iOS Rehberi */}
                  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-navy-700 dark:bg-navy-900">
                    <span className="mb-3 block text-xs font-bold uppercase tracking-widest text-gray-400">
                      iOS (iPhone/iPad) İçin
                    </span>
                    <ol className="flex flex-col gap-3 text-sm font-medium text-navy-700 dark:text-white">
                      <li className="flex items-center gap-2">
                        1. Alt menüdeki{" "}
                        <MdIosShare className="mx-1 text-lg text-brand-500" />{" "}
                        Paylaş ikonuna dokunun.
                      </li>
                      <li className="flex items-center gap-2">
                        2. Çıkan menüden{" "}
                        <MdAddBox className="mx-1 text-lg text-brand-500" />{" "}
                        'Ana Ekrana Ekle'yi seçin.
                      </li>
                    </ol>
                  </div>

                  {/* Android Rehberi */}
                  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-navy-700 dark:bg-navy-900">
                    <span className="mb-3 block text-xs font-bold uppercase tracking-widest text-gray-400">
                      Android / Masaüstü İçin
                    </span>
                    <p className="text-sm font-medium leading-relaxed text-navy-700 dark:text-white">
                      Tarayıcı menüsünden (üç nokta veya sağ üst köşe){" "}
                      <strong>"Ana Ekrana Ekle"</strong> veya{" "}
                      <strong>"Uygulamayı Yükle"</strong> seçeneğine dokunarak
                      işlemi tamamlayabilirsiniz.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="animate-fadeIn flex flex-col items-center gap-6 py-4 text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 text-brand-500 shadow-inner dark:bg-brand-500/10">
                    <FiBell className="animate-bounce text-4xl" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-navy-700 dark:text-white">
                      Anında Haberdar Olun
                    </h4>
                    <p className="mt-2 px-2 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                      Randevu onayları, iptaller ve işletmenizle ilgili en
                      önemli bildirimleri cihazınızda anında almak için izin
                      verin.
                    </p>
                  </div>
                  <button
                    className="w-full rounded-xl bg-brand-500 py-3.5 text-sm font-bold text-white shadow-lg shadow-brand-500/30 transition-all hover:bg-brand-600 active:scale-95"
                    onClick={requestNotificationPermission} // GERÇEK TARAYICI İZNİNİ TETİKLER
                  >
                    Bildirimlere İzin Ver
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
